// src/components/UiFeedbackWidget/useUiFeedbackDraft.js
//
// Spec 20260925-ui-feedback §6.1, plan T1.28. Tasks T046.
//
// The unsent draft — text, chips, picked element, pinned page — survives a navigation or
// a reload within the TAB, and nothing more:
//
//   • sessionStorage, NEVER localStorage: a shared ward workstation must not show the next
//     person someone else's half-written remark.
//   • Every read and write in try/catch: private windows, blocked site data and quota
//     errors throw, and the widget must work without storage at all.
//   • A draft that does not parse is wiped, not half-restored.

import { useCallback, useState } from 'react';

export const DRAFT_KEY = 'caro.uiFeedback.draft.v1';

export const EMPTY_DRAFT = Object.freeze({ body: '', categories: [], element: null, pinnedPage: null });

function sanitize(raw) {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return { ...EMPTY_DRAFT };
  return {
    body: typeof raw.body === 'string' ? raw.body : '',
    categories: Array.isArray(raw.categories) ? raw.categories.filter((c) => typeof c === 'string') : [],
    element: raw.element && typeof raw.element === 'object' ? raw.element : null,
    pinnedPage: raw.pinnedPage && typeof raw.pinnedPage === 'object' ? raw.pinnedPage : null,
  };
}

export function readDraft() {
  let text = null;
  try {
    text = window.sessionStorage.getItem(DRAFT_KEY);
  } catch {
    return { ...EMPTY_DRAFT };
  }
  if (!text) return { ...EMPTY_DRAFT };
  try {
    return sanitize(JSON.parse(text));
  } catch {
    try {
      window.sessionStorage.removeItem(DRAFT_KEY);
    } catch {
      /* storage unavailable */
    }
    return { ...EMPTY_DRAFT };
  }
}

function writeDraft(draft) {
  try {
    const empty =
      !draft.body && draft.categories.length === 0 && !draft.element && !draft.pinnedPage;
    if (empty) window.sessionStorage.removeItem(DRAFT_KEY);
    else window.sessionStorage.setItem(DRAFT_KEY, JSON.stringify(draft));
  } catch {
    /* storage unavailable — the in-memory draft still works */
  }
}

/** @returns {{ draft: object, update: (patch: object) => void, clear: () => void }} */
export function useUiFeedbackDraft() {
  const [draft, setDraft] = useState(readDraft);

  const update = useCallback((patch) => {
    setDraft((prev) => {
      const next = sanitize({ ...prev, ...(typeof patch === 'function' ? patch(prev) : patch) });
      writeDraft(next);
      return next;
    });
  }, []);

  const clear = useCallback(() => {
    const next = { ...EMPTY_DRAFT, categories: [] };
    writeDraft(next);
    setDraft(next);
  }, []);

  return { draft, update, clear };
}

export default useUiFeedbackDraft;
